import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "../../../../../../components/Badge/Badge.tsx";
import { Button } from "../../../../../../components/Button/Button.tsx";
import { useAppMutation } from "../../../../../../http/useAppMutation.ts";
import { COPY_AD_TOOL } from "../../../../../../routes.ts";
import { trackEvent } from "../../../../../../tracking/tracking.ts";
import { useSelectedModelsSettingsAndActions } from "../../../../../boardState/hooks/useSelectedModelsSettingsAndActions.ts";
import { useWorkspaceFonts } from "../../../../../hooks/useWorkspaceFonts.ts";
import { useWorkspaceUuid } from "../../../../../hooks/useWorkspaceUuid.ts";
import type { BrandFont } from "../../../../../types.ts";
import { ModelSelectionSection } from "../../../../components/Panel/Settings/ModelSelectionSection.tsx";
import { useBoardUuid } from "../../../../hooks/useBoardUuid.ts";
import { useSelectedAsset } from "../../../../hooks/useSelectedAsset.ts";
import { DesignerSettingsItem } from "../components/Layout/DesignerSettingsItem.tsx";
import { DesignerSettingsLayout } from "../components/Layout/DesignerSettingsLayout.tsx";
import { FontFamilySelectionDropdown } from "../EditText/components/FontFamilySelectionDropdown.tsx";
import { useAdCompositionBuilder } from "./hooks/useAdCompositionBuilder.tsx";

export const CopyAdSettings = () => {
  const workspaceUuid = useWorkspaceUuid();
  const boardUuid = useBoardUuid();
  const { selectedAsset } = useSelectedAsset();
  const { workspaceFonts, builtinFonts } = useWorkspaceFonts();
  const { selectedModelsSettings } = useSelectedModelsSettingsAndActions(
    COPY_AD_TOOL,
    boardUuid,
  );
  const [selectedFont, setSelectedFont] = useState<BrandFont | undefined>(
    builtinFonts.at(0),
  );
  const copyAdMutation = useAppMutation({
    path: "boards/copy-ad",
    invalidate: [
      `workspaces/${workspaceUuid}/boards`,
      `boards/${boardUuid}/assets`,
    ],
    onSuccess: () => {
      trackEvent(
        "content:generate",
        {
          generation_type: "CopyAd",
        },
        true,
      );
    },
  }).mutation;

  const { status } = useAdCompositionBuilder();

  const queryClient = useQueryClient();

  return (
    selectedAsset && (
      <DesignerSettingsLayout>
        <ModelSelectionSection
          tool={COPY_AD_TOOL}
          doesObjectModelSupportUpload
        />
        <DesignerSettingsItem
          title="Font Family"
          rightItem={
            <FontFamilySelectionDropdown
              fontFamily={selectedFont?.family ?? ""}
              onFontFamilyChange={(value) => {
                let font = undefined;
                if (
                  (font =
                    builtinFonts.find((f) => f.family === value) ??
                    workspaceFonts.find((f) => f.family === value))
                ) {
                  setSelectedFont(font);
                }
              }}
            />
          }
        />
        <DesignerSettingsItem
          title="Status"
          rightItem={
            <Badge label={status.upperFirst()} size="md" color="neutral" />
          }
        />
        <Button
          loading={status === "loading" || copyAdMutation.isLoading}
          onClick={() => {
            copyAdMutation.mutate(
              {
                board_uuid: boardUuid,
                asset_uuid: selectedAsset.uuid,
                reference_uuid: selectedModelsSettings.generationParams[0].uuid,
                font_uuid: selectedFont?.uuid,
              },
              {
                onSuccess: () => {
                  [
                    `assets/${selectedAsset.uuid}`,
                    `assets/${selectedAsset.uuid}/history`,
                  ].map(
                    (queryKey) =>
                      void queryClient.invalidateQueries({
                        queryKey: [queryKey],
                      }),
                  );
                },
              },
            );
          }}
        >
          Copy Ad
        </Button>
      </DesignerSettingsLayout>
    )
  );
};
